import { linkOptions, useNavigate, useSearch } from '@tanstack/react-router';

import { UserGatewayMessage } from '../../../../types/UserGatewayMessage';
import { RadioCardVariants } from '../../../cdl/RadioCard/components/RadioCard';
import { RadioCardGroup } from '../../../cdl/RadioCard/RadioCardGroup';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { CompanyListing } from '../../../common/CompanyListing/CompanyListing';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { getSortedAccessCompanies } from '../../../common/helpers/getSortedAccessCompanies.helper';
import { identifyUserRole } from '../../../common/helpers/identifyUserRole.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { useRole } from '../../../common/hooks/useRole';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../common/ui/Box';
import { useManagementUsersList } from '../hooks/useManagementUsersList';
import { MemberQuickFilter, useMemberQuickFilter } from '../hooks/useMemberQuickFilter';

import { MemberDropdown } from './MemberDropdown';
import { MemberNameCell } from './MemberNameCell';
import { MembersFilterPills } from './MembersFilterPills';
import { NoMembersState } from './NoMembersState';
import { NoSearchResultsState } from './NoSearchResultsState';
import { ScrollableTableContainer } from './ScrollableTableContainer';

interface MembersTableProps {
    groupId: string;
}

export const MembersTable = ({ groupId }: MembersTableProps) => {
    const { getHumanReadableValue } = useEnums();
    const role = useRole();

    const navigate = useNavigate({
        from: role.isCustomer() ? '/customer-groups/$groupId/members/' : '/supplier-groups/$groupId/members/',
    });
    const search = useSearch({
        from: role.isCustomer() ? '/_app/customer-groups/$groupId/members/' : '/_app/supplier-groups/$groupId/members/',
    });

    const usersListQuery = useManagementUsersList({
        companyIds: role.isCustomer() ? search.customerIds : search.supplierIds,
        groupIds: [groupId],
        searchQuery: search.searchQuery,
        statuses: search.status ? [search.status] : undefined,
    });

    const { selectedFilter, setSelectedFilter, filterOptions } = useMemberQuickFilter({
        searchQuery: search.searchQuery,
        companyIds: role.isCustomer() ? search.customerIds : search.supplierIds,
        groupIds: [groupId],
        currentStatus: search.status,
        onStatusChange: (status) => {
            navigate({
                search: (previousSearch) => ({
                    ...previousSearch,
                    status,
                }),
            });
        },
    });

    if (usersListQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (usersListQuery.isError) {
        return <AdvancedError />;
    }

    const buildRowLink = (user: UserGatewayMessage) => {
        if (role.isCustomer()) {
            return linkOptions({
                to: '/customer-groups/$groupId/members/$userId',
                params: { groupId, userId: user.id },
            });
        } else {
            return linkOptions({
                to: '/supplier-groups/$groupId/members/$userId',
                params: { groupId, userId: user.id },
            });
        }
    };

    return (
        <Box display="flex" flexDirection="column" height="100%" gap="12px">
            <RadioCardGroup
                options={filterOptions}
                value={selectedFilter}
                onChange={setSelectedFilter}
                variant={RadioCardVariants.SLIM}
            />

            <MembersFilterPills allEntries={usersListQuery.data}>
                {(filteredUsers) => (
                    <ScrollableTableContainer
                        scrollToTopTrigger={JSON.stringify({ selectedFilter, urlFilter: search.filter })}
                    >
                        {filteredUsers.length === 0 && !usersListQuery.isPending ? (
                            usersListQuery.data?.length === 0 && selectedFilter === MemberQuickFilter.ALL ? (
                                <NoMembersState />
                            ) : (
                                <NoSearchResultsState />
                            )
                        ) : null}

                        {filteredUsers.length > 0 ? (
                            <TableBuilder
                                data={filteredUsers}
                                rowLink={buildRowLink}
                                grow={false}
                                isLoading={usersListQuery.isPlaceholderData && usersListQuery.isFetching}
                            >
                                <TableBuilderColumn<UserGatewayMessage> header={translate('user.name')} width="20%">
                                    {(user) => <MemberNameCell user={user} />}
                                </TableBuilderColumn>

                                <TableBuilderColumn<UserGatewayMessage> header={translate('user.mail')} truncate>
                                    {(user) => user.email}
                                </TableBuilderColumn>

                                <TableBuilderColumn<UserGatewayMessage> header={translate('user.role')} width="120px">
                                    {(user) =>
                                        getHumanReadableValue(
                                            'role',
                                            identifyUserRole(user.accessLevel, user.companyAccesses)
                                        )
                                    }
                                </TableBuilderColumn>

                                <TableBuilderColumn<UserGatewayMessage>
                                    header={translate('user.companyColumn')}
                                    width="20%"
                                >
                                    {(user) => {
                                        const companies = getSortedAccessCompanies(user.companyAccesses);

                                        if (!companies) {
                                            return null;
                                        }

                                        return <CompanyListing companies={companies} />;
                                    }}
                                </TableBuilderColumn>

                                <TableBuilderColumn<UserGatewayMessage> header="" width="64px" interactive>
                                    {(user) => <MemberDropdown user={user} />}
                                </TableBuilderColumn>
                            </TableBuilder>
                        ) : null}
                    </ScrollableTableContainer>
                )}
            </MembersFilterPills>
        </Box>
    );
};
